import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import SEO from "../components/SEO";
import emailjs from "@emailjs/browser";

const icons = {
  location: (
    <svg
      className="w-5 h-5 text-[#C5A047]"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  ),
  email: (
    <svg
      className="w-5 h-5 text-[#C5A047]"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  ),
  hours: (
    <svg
      className="w-5 h-5 text-[#C5A047]"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="9" strokeWidth="2" />
      <path
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 7v5l3 2"
      />
    </svg>
  ),
  leadership: (
    <svg
      className="w-5 h-5 text-[#C5A047]"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M10 11a4 4 0 100-8 4 4 0 000 8ZM20 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"
      />
    </svg>
  ),
  capabilities: (
    <svg
      className="w-5 h-5 text-[#C5A047]"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="8" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="2.5" strokeWidth="1.8" />
      <path strokeWidth="1.8" d="M12 4v2M12 18v2M4 12h2M18 12h2" />
    </svg>
  ),
  success: (
    <svg
      className="w-10 h-10 text-green-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M5 13l4 4L19 7"
      />
    </svg>
  ),
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      emailjs.init(process.env.REACT_APP_EMAILJS_PUBLIC_KEY);

      const templateParams = {
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone || "Not provided",
        subject: formData.subject,
        message: formData.message,
        date: new Date().toLocaleDateString("en-US"),
        inquiry_type: "General Contact",
      };

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_CONTACT_TEMPLATE_ID,
        templateParams
      );

      setSubmitted(true);
      setLoading(false);

      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: "",
          company: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      }, 8000);
    } catch (error) {
      console.error("Error sending contact form:", error);
      setSubmitted(true);
      setLoading(false);

      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: "",
          company: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      }, 8000);
    }
  };

  return (
    <>
      <SEO
        title="Contact Us | Providence Federal Group"
        description="Get in touch with Providence Federal Group for federal contracting inquiries, partnership opportunities, and general questions."
        keywords="contact federal contractor, government contracting, Rhode Island SDVOSB"
      />

      <div className="min-h-screen bg-gray-50">
        <Header />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#1E3A8A] via-[#2C3E50] to-[#0F1A3A] text-white pt-40 pb-24 overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="max-w-7xl mx-auto px-4 relative z-10 mt-20">
            <div className="text-center max-w-3xl mx-auto">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Contact Us
              </h1>
              <p className="text-xl md:text-2xl mb-10 leading-relaxed">
                Get in touch with Providence Federal Group for federal
                contracting opportunities, partnership inquiries, or general
                questions about our services.
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Left Column: Contact Form */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-10">
                  <div className="mb-10">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      Send Us a Message
                    </h2>
                    <p className="text-gray-600">
                      Fill out the form below and our team will get back to you
                      within 24-48 hours.
                    </p>
                  </div>

                  {submitted ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        {icons.success}
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">
                        Message Sent!
                      </h3>
                      <p className="text-gray-600 mb-8 max-w-md mx-auto">
                        Thank you for contacting Providence Federal Group.
                        We&apos;ll get back to you shortly.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-8">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Full Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C5A047] focus:border-transparent transition-all"
                            placeholder="John Smith"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Company / Organization
                          </label>
                          <input
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C5A047] focus:border-transparent transition-all"
                            placeholder="Your Company"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C5A047] focus:border-transparent transition-all"
                            placeholder="john.smith@email.com"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C5A047] focus:border-transparent transition-all"
                            placeholder="(555) 123-4567"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Subject *
                        </label>
                        <select
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C5A047] focus:border-transparent transition-all"
                        >
                          <option value="">Select a subject...</option>
                          <option value="Federal Contracting">
                            Federal Contracting Inquiry
                          </option>
                          <option value="Partnership">
                            Partnership Opportunity
                          </option>
                          <option value="General Question">
                            General Question
                          </option>
                          <option value="Request for Proposal">
                            Request for Proposal
                          </option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Message *
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows="6"
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#C5A047] focus:border-transparent transition-all"
                          placeholder="How can we help you?"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-4 font-bold rounded-xl transition-all duration-300 ${
                          loading
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-gradient-to-r from-[#C5A047] to-[#D4AF37] hover:from-[#D4AF37] hover:to-[#C5A047] shadow-lg hover:shadow-xl"
                        } text-white`}
                      >
                        {loading ? (
                          <div className="flex items-center justify-center">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                            Sending Message...
                          </div>
                        ) : (
                          "Send Message"
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                {/* Contact Information Card */}
                <div className="bg-gradient-to-br from-[#1E3A8A] to-[#2C3E50] text-white p-8 rounded-2xl shadow-xl">
                  <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Contact Information
                  </h2>

                  <div className="space-y-6">
                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                        {icons.location}
                      </div>
                      <div>
                        <h4 className="font-bold mb-1">Our Location</h4>
                        <p className="font-medium">
                          Providence&apos;s Financial District
                        </p>
                        <p>10 Dorrance Street Suite 700</p>
                        <p>Providence, RI 02903</p>
                        <p>United States</p>
                        <p className="text-white/60 text-sm mt-1">
                          Serving federal clients nationwide
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                        {icons.email}
                      </div>
                      <div>
                        <h4 className="font-bold mb-1">Email</h4>
                        <a
                          href="mailto:support@providencefederal.com"
                          className="text-white/80 hover:text-white transition-colors"
                        >
                          support@providencefederal.com
                        </a>
                        <p className="text-white/60 text-sm">
                          General inquiries
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start">
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                        {icons.hours}
                      </div>
                      <div>
                        <h4 className="font-bold mb-1">Business Hours</h4>
                        <p className="text-white/80">
                          Monday - Friday: 8:00 AM - 6:00 PM EST
                        </p>
                        <p className="text-white/60 text-sm">
                          Response within 24-48 hours
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick Links Card */}
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-200">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    Quick Links
                  </h2>

                  <div className="space-y-4">
                    <Link
                      to="/leadership"
                      className="flex items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#C5A047]/10 rounded-lg flex items-center justify-center mr-4">
                        {icons.leadership}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">
                          Meet Our Leadership
                        </h4>
                        <p className="text-gray-600 text-sm">
                          Learn about our executive team
                        </p>
                      </div>
                    </Link>

                    <Link
                      to="/capabilities"
                      className="flex items-center p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#C5A047]/10 rounded-lg flex items-center justify-center mr-4">
                        {icons.capabilities}
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">
                          Our Capabilities
                        </h4>
                        <p className="text-gray-600 text-sm">
                          Explore our services
                        </p>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;